import type { TFunction } from "i18next";
import type { SearchableDropdownOption } from "components/designV2/searchableDropdown/searchableDropdown.types";
import {
  EMPLOYEE_CATEGORY_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
  SALARY_OPTIONS,
} from "model/OrganizationalStructure";
import { PaymentMethod } from "model/Gig";
import { SalaryType } from "model/Salary";
import type { UnitTeamOption } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import type { AddStaffOption } from "./addStaffPanel.contract";
import type { EmploymentUiRules } from "../shared/staffEmploymentRules.logic";
import { PAYMENT_METHOD_OPTIONS } from "../shared/staff.paymentMethod.logic";
import {
  toEmploymentTypeOptions,
  toTranslatedOptions,
} from "../shared/staffPanel.options.logic";
import { toVacationPayoutOptions } from "../shared/staffVacation.logic";

export interface AddStaffOptions {
  readonly employeeCategoryOptions: AddStaffOption[];
  readonly employmentOptions: AddStaffOption[];
  readonly paymentMethodOptions: AddStaffOption[];
  readonly paymentOptions: AddStaffOption[];
  readonly salaryOptions: AddStaffOption[];
  readonly teamOptions: SearchableDropdownOption[];
  readonly vacationPayoutOptions: AddStaffOption[];
}

export function getSalaryAmountLabel({
  salaryType,
  translate,
}: {
  readonly salaryType?: string;
  readonly translate: TFunction;
}): string {
  if (salaryType === SalaryType.Monthly) {
    return translate("Staff.AddStaffPanelFieldSalaryAmountMonthly");
  }
  if (salaryType === SalaryType.Hourly) {
    return translate("Staff.AddStaffPanelFieldSalaryAmountHourly");
  }
  return translate("Staff.AddStaffPanelFieldSalaryAmount");
}

function toTeamOptions(
  unitTeamOptions: UnitTeamOption[],
): SearchableDropdownOption[] {
  return unitTeamOptions.flatMap((unit): SearchableDropdownOption[] => {
    const unitName = (unit.unitName ?? "").trim();
    return unit.teams.map((team) => {
      const teamName = (team.name ?? "").trim();
      return {
        value: String(team.id),
        label: unitName.length > 0 ? `${unitName} - ${teamName}` : teamName,
      };
    });
  });
}

function toSalaryOptions({
  employmentRules,
  translate,
}: {
  readonly employmentRules: EmploymentUiRules;
  readonly translate: TFunction;
}): AddStaffOption[] {
  const options = toTranslatedOptions(SALARY_OPTIONS, translate);
  return employmentRules.isSimflexityEmployee ||
    employmentRules.isHourlyTemporaryEmployee
    ? options.filter((option) => option.value !== SalaryType.Monthly)
    : options;
}

function toPaymentOptions({
  employmentRules,
  translate,
}: {
  readonly employmentRules: EmploymentUiRules;
  readonly translate: TFunction;
}): AddStaffOption[] {
  const options = toTranslatedOptions(PAYMENT_TYPE_OPTIONS, translate);
  if (employmentRules.isSimflexityEmployee) {
    return options.filter(
      (option) => option.value === PaymentMethod.SIMFLEXITY_PAY,
    );
  }
  return options.filter((option) => {
    return (
      option.value !== PaymentMethod.SIMFLEXITY_PAY ||
      employmentRules.canSelectSimflexityPay
    );
  });
}

export function getAddStaffOptions({
  employmentRules,
  translate,
  unitTeamOptions,
}: {
  readonly employmentRules: EmploymentUiRules;
  readonly translate: TFunction;
  readonly unitTeamOptions: UnitTeamOption[];
}): AddStaffOptions {
  return {
    employeeCategoryOptions: toTranslatedOptions(
      EMPLOYEE_CATEGORY_OPTIONS,
      translate,
    ),
    employmentOptions: toEmploymentTypeOptions({
      canSelectSimflexityEmployee: employmentRules.canSelectSimflexityEmployee,
      translate,
    }),
    paymentMethodOptions: PAYMENT_METHOD_OPTIONS.map(
      (paymentMethodOption): AddStaffOption => ({
        value: paymentMethodOption.value,
        label: translate(paymentMethodOption.label),
      }),
    ),
    paymentOptions: toPaymentOptions({ employmentRules, translate }),
    salaryOptions: toSalaryOptions({ employmentRules, translate }),
    teamOptions: toTeamOptions(unitTeamOptions),
    vacationPayoutOptions: toVacationPayoutOptions(translate),
  };
}
